import { useContentfulImages } from '@snapchat/mw-contentful-client';
import {
  CardStackCard as CardStackCardSDS,
  Media as MediaSDS,
  SnapchatEmbed as SnapchatEmbedSDS,
} from '@snapchat/snap-design-system-marketing';
import type { FC, ReactNode } from 'react';

import { useParseMediaContainer } from '../../../hooks/useParseMediaContainer';
import { combineImageSources } from '../../../utils/combineImageSources';
import { renderRichText } from '../../../utils/renderText/renderRichText';
import { getImageSourceSettings } from '../../Image';
import type { ImageDataProps } from '../../Image/types';
import type { SnapchatEmbedDataProps } from '../../SnapchatEmbed';
import type { VideoDataProps } from '../../Video/types';
import type { CardStackCardDataProps } from './types';

export const CardStackCard: FC<CardStackCardDataProps> = ({
  aspectRatio,
  theme,
  body,
  media,
  title,
  url,
}) => {
  const { getImageSources } = useContentfulImages();
  let mediaElement: ReactNode = null;
  const typeName = media.__typename;

  const {
    media: { imageSource, imageAltText, videoSource, contentType },
    mobileMedia: {
      imageSource: mobileImageSource,
      videoSource: mobileVideoSource,
      contentType: mobileContentType,
    },
    thumbnailSource,
  } = useParseMediaContainer(media as ImageDataProps | VideoDataProps);

  if (typeName === 'SnapchatEmbed') {
    mediaElement = <SnapchatEmbedSDS {...(media as SnapchatEmbedDataProps)} />;
  } else if (typeName === 'Image') {
    const image = media as ImageDataProps;

    const { desktopSettings, mobileSettings } = getImageSourceSettings({
      desktopHeight: image.media?.height ?? 0,
      mobileHeight: image.mobileMedia?.height ?? 0,
      enableHighDpi: image.enableHighDpi,
      quality: image.quality,
    });

    const imgSrcs = combineImageSources({
      desktop: getImageSources(imageSource, desktopSettings),
      mobile: getImageSources(mobileImageSource ?? imageSource, mobileSettings),
    });

    mediaElement = <MediaSDS imgSrcs={imgSrcs} altText={imageAltText} />;
  } else {
    const videoProps = {
      videoSource,
      mobileVideoSource,
      mediaContentType: contentType,
      mobileMediaContentType: mobileContentType,
      posterSource: thumbnailSource,
    };

    mediaElement = <MediaSDS {...videoProps} />;
  }

  return (
    <CardStackCardSDS
      theme={theme}
      aspectRatio={aspectRatio}
      body={renderRichText(body)}
      media={mediaElement}
      title={title}
      url={url}
    />
  );
};

CardStackCard.displayName = 'CardStackCard';
